(() => {
  let referenceCount = 0;
  const highlightTimers = new WeakMap();
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isFootnoteTarget = target => Boolean(
    target && (
      /footnote/i.test(target.id || '') ||
      target.hasAttribute('data-footnote-target') ||
      target.hasAttribute('data-abino-footnote') ||
      target.hasAttribute('data-abino-footnote-source')
    )
  );

  const getHighlightElement = target => target.querySelector('[data-footnote-text]') || target;

  const setHighlight = (element, active) => {
    if (!element) return;
    element.classList.toggle('is-footnote-highlighted', active);
  };

  const flashHighlight = element => {
    if (!element) return;
    window.clearTimeout(highlightTimers.get(element));
    setHighlight(element, true);
    const timer = window.setTimeout(() => setHighlight(element, false), 1800);
    highlightTimers.set(element, timer);
  };

  const ensureReferenceId = reference => {
    if (reference.id) return reference.id;
    let id;
    do {
      referenceCount += 1;
      id = `AbinoFootnoteReference-${referenceCount}`;
    } while (document.getElementById(id));
    reference.id = id;
    return id;
  };

  const navigateTo = (element, id) => {
    history.replaceState(null, '', `#${id}`);
    element.scrollIntoView({
      behavior: reduceMotion() ? 'auto' : 'smooth',
      block: 'center'
    });
    element.focus({ preventScroll: true });
  };

  const initTarget = target => {
    if (target.dataset.abinoFootnoteTargetReady === 'true') return;
    target.dataset.abinoFootnoteTargetReady = 'true';
    target.setAttribute('tabindex', '0');

    const highlightElement = getHighlightElement(target);
    const highlight = () => setHighlight(highlightElement, true);
    const clearHighlight = () => setHighlight(highlightElement, false);
    const isInteractiveChild = eventTarget => Boolean(eventTarget.closest('a, button, input, select, textarea, summary'));
    const returnToReference = () => {
      const returnId = target.dataset.abinoFootnoteReturnId;
      const reference = returnId ? document.getElementById(returnId) : null;
      if (!reference) return;
      navigateTo(reference, returnId);
      setHighlight(highlightElement, false);
      flashHighlight(reference);
    };

    target.addEventListener('mouseenter', highlight);
    target.addEventListener('mouseleave', clearHighlight);
    target.addEventListener('focusin', highlight);
    target.addEventListener('focusout', clearHighlight);
    target.addEventListener('click', event => {
      if (isInteractiveChild(event.target)) return;
      returnToReference();
    });
    target.addEventListener('keydown', event => {
      if (isInteractiveChild(event.target) || (event.key !== 'Enter' && event.key !== ' ')) return;
      event.preventDefault();
      returnToReference();
    });
  };

  const initReference = reference => {
    if (reference.dataset.abinoFootnoteReady === 'true') return;
    const id = decodeURIComponent(reference.hash.slice(1));
    const target = document.getElementById(id);
    if (!isFootnoteTarget(target)) return;

    reference.dataset.abinoFootnoteReady = 'true';
    initTarget(target);
    const highlightElement = getHighlightElement(target);
    const highlight = () => setHighlight(highlightElement, true);
    const clearHighlight = () => setHighlight(highlightElement, false);

    reference.addEventListener('mouseenter', highlight);
    reference.addEventListener('mouseleave', clearHighlight);
    reference.addEventListener('focus', highlight);
    reference.addEventListener('blur', clearHighlight);
    reference.addEventListener('click', event => {
      event.preventDefault();
      const details = target.closest('details');
      if (details) details.open = true;
      target.dataset.abinoFootnoteReturnId = ensureReferenceId(reference);
      navigateTo(target, target.id);
      flashHighlight(highlightElement);
    });
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('a[href^="#"]').forEach(initReference);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', event => initAll(event.target));
})();
